import { roundCurrency } from './money'
import type { BookingFinancialInputs, RefundQuoteData } from './types'

/**
 * A voluntary change reissues the tickets, and Sabre keeps listing the replaced ones beside the new
 * ones (SPEC.md §2a). PLT2638138: two passengers, four tickets, the old pair with every coupon
 * Exchanged. Counted as live, they made a full cancellation look partial and doubled the cost.
 */

/** Sabre's coupon status for a ticket that a reissue replaced. */
const EXCHANGED = 'E'

/** Plan totals are whole cents apart at most when they really match. */
const PLAN_TOTAL_TOLERANCE = 1

export function isExchangedTicket(rqd: RefundQuoteData, ticketNumber: string): boolean {
  const coupons = rqd.ticketStatusByTicket?.[ticketNumber]?.coupons ?? []
  return coupons.length > 0 && coupons.every(coupon => coupon.code === EXCHANGED)
}

/** The quote without the tickets a reissue replaced: they are no longer documents anyone can refund. */
export function withoutExchangedTickets(rqd: RefundQuoteData): RefundQuoteData {
  const tickets = rqd.tickets ?? []
  const live = tickets.filter(ticket => !isExchangedTicket(rqd, ticket.number))
  return live.length === tickets.length ? rqd : { ...rqd, tickets: live }
}

export interface ExchangeResolution {
  exchanged: boolean
  /** What PLT paid the supplier for the change, on top of the original tickets. */
  supplierChangeFee: number
  /** The customer's change payment when it was taken outside the payment plan; 0 otherwise. */
  customerChangeFeeOutsidePlan: number
  warnings: string[]
}

/**
 * Whether the booking was changed, and what the change cost each side. The customer's change payment
 * counts towards what they paid only when the plan total shows it was taken outside the plan: 382 of
 * 1,614 changed bookings with a plan have it inside, where the payments API already counts it.
 */
export function resolveExchange(rqd: RefundQuoteData, booking: BookingFinancialInputs): ExchangeResolution {
  const supplierChangeFee = booking.voluntaryChange?.vcSupplierChangeFeePaid ?? 0
  const customerChangeFee = booking.voluntaryChange?.vcCustomerChangeFeeCredFdPaid ?? 0
  const hasExchangedTicket = Object.keys(rqd.ticketStatusByTicket ?? {}).some(n => isExchangedTicket(rqd, n))
  const exchanged = hasExchangedTicket || supplierChangeFee > 0

  if (!exchanged) return { exchanged, supplierChangeFee: 0, customerChangeFeeOutsidePlan: 0, warnings: [] }

  const warnings = [
    'exchanged-booking: the tickets were reissued by a change — the GDS fare and taxes span both issues, so the ' +
      'cost is the original net fare plus the supplier change fee',
  ]
  const planTotal = booking.payment?.planTotal
  const matches = (a: number, b: number) => Math.abs(a - b) <= PLAN_TOTAL_TOLERANCE

  let customerChangeFeeOutsidePlan = 0
  if (customerChangeFee > 0) {
    if (planTotal != null && matches(planTotal, booking.value)) {
      customerChangeFeeOutsidePlan = roundCurrency(customerChangeFee)
    } else if (planTotal == null || !matches(planTotal, booking.value + customerChangeFee)) {
      warnings.push(
        `change-fee-payment-unknown: the customer paid ${customerChangeFee} for the change, but the plan does not ` +
          'show whether that was inside it — check what they paid before quoting',
      )
    }
  }

  return { exchanged, supplierChangeFee: roundCurrency(supplierChangeFee), customerChangeFeeOutsidePlan, warnings }
}
